import React from 'react';
import styled from 'styled-components';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { BarChart3, Database, Settings, LogOut, Users, ChevronDown, Menu, X, Mail, Clock3, ClipboardList, TrendingUp, Package, Table } from 'lucide-react';
import FilialSelector from './FilialSelector';
import { useFiliais } from '@/hooks/useFiliais';
import { useFilialSelecionada } from '@/hooks/useFilialSelecionada';
import { useAuth } from '@/context/AuthContext';
import type { Filial } from '@/types';

const LayoutContainer = styled.div`
  min-height: 100vh;
  background-color: #f9fafb;
`;

const Header = styled.header`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 16px 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1000;
  @media (max-width: 768px) {
    padding: 12px 16px;
  }
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 24px;
  font-weight: bold;
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  background: rgba(255,255,255,0.08);
  color: white;
  border: none;
  cursor: pointer;
  &:hover { background: rgba(255,255,255,0.14); }
  @media (max-width: 768px) {
    display: inline-flex;
  }
`;

const Navigation = styled.nav<{ open: boolean }>`
  display: flex;
  gap: 24px;
  @media (max-width: 768px) {
    display: ${({ open }) => (open ? 'flex' : 'none')};
    flex-direction: column;
    gap: 8px;
    width: 100%;
    padding-top: 12px;
  }
`;

const NavLink = styled.a`
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 6px;
  transition: background-color 0.2s;
  font-weight: 500;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const NavLinkRouter = styled(Link)`
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 6px;
  transition: background-color 0.2s;
  font-weight: 500;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const Dropdown = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 6px;
  background: transparent;
  color: white;
  border: none;
  font-family: inherit;
  font-weight: 500;
  font-size: inherit;
  line-height: inherit;
  text-decoration: none;
  cursor: pointer;
  &:hover { background-color: rgba(255,255,255,0.1); }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 40px;
  left: 0;
  background: white;
  color: #111827;
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05);
  min-width: 200px;
  overflow: hidden;
  z-index: 2000;
`;

const DropdownItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  color: #111827;
  text-decoration: none;
  &:hover { background: #f3f4f6; }
`;

const Main = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const ContentArea = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  min-height: 600px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: #6b7280;
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  margin-bottom: 16px;
  color: #d1d5db;
`;

const EmptyStateTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
`;

const EmptyStateDescription = styled.p`
  margin: 0;
  font-size: 14px;
  max-width: 400px;
  line-height: 1.5;
`;

const Layout: React.FC = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const adminRoutesWithFilialSelector = ['/admin/ponto-escalas'];
  const shouldShowFilialSelector = !isAdminRoute || adminRoutesWithFilialSelector.some((route) => location.pathname.startsWith(route));
  const { filiais, isLoading } = useFiliais({ enabled: shouldShowFilialSelector });
  const { filialSelecionada, selecionarFilial } = useFilialSelecionada();
  const { logout, user } = useAuth();
  const [openCadastros, setOpenCadastros] = React.useState(false);
  const [openRelatorios, setOpenRelatorios] = React.useState(false);
  const [openConfiguracoes, setOpenConfiguracoes] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  return (
    <LayoutContainer>
      <Header>
        <HeaderContent>
          <Logo>
            <NavLinkRouter to="/">
              <Database size={32} />
              Gutierrez Multibanco
            </NavLinkRouter>
          </Logo>

          <MobileMenuButton onClick={() => setMobileMenuOpen((v) => !v)} aria-label="Abrir menu">
            {mobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
          </MobileMenuButton>

          <Navigation open={mobileMenuOpen}>
            {user?.is_admin && (
              <Dropdown onMouseLeave={() => setOpenCadastros(false)}>
                <DropdownButton onMouseEnter={() => setOpenCadastros(true)} onClick={() => setOpenCadastros(!openCadastros)}>
                  <ClipboardList size={18} />
                  Cadastros
                  <ChevronDown size={16} />
                </DropdownButton>
                {openCadastros && (
                  <DropdownMenu>
                    <DropdownItem to="/admin/usuarios" onClick={() => setOpenCadastros(false)}>
                      <Users size={16} /> Usuários
                    </DropdownItem>
                    <DropdownItem to="/admin/schedules" onClick={() => setOpenCadastros(false)}>
                      <Mail size={16} /> Agendamentos de e-mail
                    </DropdownItem>
                    <DropdownItem to="/admin/tire-alert-rules" onClick={() => setOpenCadastros(false)}>
                      <Package size={16} /> Regras de alerta de pneus
                    </DropdownItem>
                    <DropdownItem to="/admin/callmebot" onClick={() => setOpenCadastros(false)}>
                      <Mail size={16} /> Interação com Whatsapp
                    </DropdownItem>
                    <DropdownItem to="/admin/ponto-escalas" onClick={() => setOpenCadastros(false)}>
                      <Clock3 size={16} /> Escalas de horário
                    </DropdownItem>
                  </DropdownMenu>
                )}
              </Dropdown>
            )}
            {user?.is_admin && (
              <Dropdown onMouseLeave={() => setOpenRelatorios(false)}>
                <DropdownButton onMouseEnter={() => setOpenRelatorios(true)} onClick={() => setOpenRelatorios(!openRelatorios)}>
                  <BarChart3 size={18} />
                  Relatórios
                  <ChevronDown size={16} />
                </DropdownButton>
                {openRelatorios && (
                  <DropdownMenu>
                    <DropdownItem to="/?grafico=total-vendas" onClick={() => setOpenRelatorios(false)}>
                      <BarChart3 size={16} /> Gráfico Total de Vendas
                    </DropdownItem>
                    <DropdownItem to="/relatorios/vendas" onClick={() => setOpenRelatorios(false)}>
                      <TrendingUp size={16} /> Relatório de recebimentos por NC
                    </DropdownItem>
                    <DropdownItem to="/relatorios/heatmap-vendas" onClick={() => setOpenRelatorios(false)}>
                      <BarChart3 size={16} /> Heatmap de Vendas (Dia x Hora)
                    </DropdownItem>
                    <DropdownItem to="/relatorios/comissoes" onClick={() => setOpenRelatorios(false)}>
                      <TrendingUp size={16} /> Relatório de Comissões por Técnico
                    </DropdownItem>
                    <DropdownItem to="/relatorios/media-vendas-vendedor" onClick={() => setOpenRelatorios(false)}>
                      <TrendingUp size={16} /> Relatório Média de Vendas por Vendedor
                    </DropdownItem>
                    <DropdownItem to="/relatorios/telemarketing-pneus-vendas" onClick={() => setOpenRelatorios(false)}>
                      <TrendingUp size={16} /> Telemarketing x Vendas de Pneus
                    </DropdownItem>
                    <DropdownItem to="/relatorios/pneus-sem-obrigatorios" onClick={() => setOpenRelatorios(false)}>
                      <Package size={16} /> Pneus sem itens obrigatórios
                    </DropdownItem>
                    <DropdownItem to="/relatorios/pneus-estoque-alerta" onClick={() => setOpenRelatorios(false)}>
                      <Package size={16} /> Pneus - Alerta de Estoque
                    </DropdownItem>
                    <DropdownItem to="/relatorios/pneus-por-nc" onClick={() => setOpenRelatorios(false)}>
                      <Package size={16} /> Vendas por NC
                    </DropdownItem>
                    <DropdownItem to="/relatorios/ponto" onClick={() => setOpenRelatorios(false)}>
                      <Clock3 size={16} /> Ponto
                    </DropdownItem>
                    <DropdownItem to="/relatorios/produtos" onClick={() => setOpenRelatorios(false)}>
                      <Package size={16} /> Grupo, Medidas e Produtos
                    </DropdownItem>
                    <DropdownItem to="/admin/produtos-por-filial" onClick={() => setOpenRelatorios(false)}>
                      <Package size={16} /> Produtos por Filial
                    </DropdownItem>
                    <DropdownItem to="/relatorios/clientes" onClick={() => setOpenRelatorios(false)}>
                      <Users size={16} /> Relatório de Clientes
                    </DropdownItem>
                    <DropdownItem to="/tabelas" onClick={() => setOpenRelatorios(false)}>
                      <Table size={16} /> Explorador de Tabelas
                    </DropdownItem>
                  </DropdownMenu>
                )}
              </Dropdown>
            )}
            {user?.is_admin && (
              <NavLinkRouter to="/assinaturas/holerite">
                <Mail size={18} />
                Assinaturas (Holerite)
              </NavLinkRouter>
            )}
            {user?.is_admin && (
              <Dropdown onMouseLeave={() => setOpenConfiguracoes(false)}>
                <DropdownButton onMouseEnter={() => setOpenConfiguracoes(true)} onClick={() => setOpenConfiguracoes(!openConfiguracoes)}>
                  <Settings size={18} />
                  Configurações
                  <ChevronDown size={16} />
                </DropdownButton>
                {openConfiguracoes && (
                  <DropdownMenu>
                    <DropdownItem to="/admin/email-config" onClick={() => setOpenConfiguracoes(false)}>
                      <Mail size={16} /> Configuração de e-mail
                    </DropdownItem>
                    <DropdownItem to="/admin/alterar-preco" onClick={() => setOpenConfiguracoes(false)}>
                      <Settings size={16} /> Alterar Preço
                    </DropdownItem>
                  </DropdownMenu>
                )}
              </Dropdown>
            )}
            <NavLink
              href="#sair"
              onClick={(e) => {
                e.preventDefault();
                logout();
              }}
              title={user?.email || 'Sair'}
            >
              <LogOut size={18} />
              Sair
            </NavLink>
          </Navigation>
        </HeaderContent>
      </Header>

      <Main>
        {shouldShowFilialSelector && (
          <FilialSelector
            filialSelecionada={filialSelecionada}
            filiais={filiais as Filial[]}
            onFilialChange={selecionarFilial}
            isLoading={isLoading}
          />
        )}

        <ContentArea>
          {isAdminRoute || filialSelecionada ? (
            <Outlet />
          ) : (
            <EmptyState>
              <EmptyStateIcon>
                <Database size={64} />
              </EmptyStateIcon>
              <EmptyStateTitle>Selecione uma Filial</EmptyStateTitle>
              <EmptyStateDescription>
                Para visualizar os relatórios e dados, selecione uma filial no seletor acima.
                Certifique-se de que a filial esteja com status &quot;Conectado&quot; para acessar os dados.
              </EmptyStateDescription>
            </EmptyState>
          )}
        </ContentArea>
      </Main>
    </LayoutContainer>
  );
};

export default Layout;
